/**
 * AIWARI — Three Winds faction system.
 *
 * Three sides war for the Stormveil — a continent perpetually battered by
 * three competing winds that shape culture, magic, and politics. Each side
 * has 5 sub-factions ("Houses" for Tidals, "Clans" for Rooted, "Bands" for
 * Anchorless) — these add identity + lore + faction-specific quests but
 * the core 3-realm RvR is unchanged.
 *
 * Player picks a side AND a sub-faction at character creation. Sub-faction
 * is per-character (not account), so an account can host loyalists of all
 * three sides if the player wants.
 */

export type Side = 'tidal' | 'rooted' | 'anchorless';

export interface SubFaction {
  id: string;
  side: Side;
  /** Display name (Russian primary, English fallback). */
  name: { ru: string; en: string };
  /** One-sentence pitch for the faction selector UI. */
  blurb: { ru: string; en: string };
  /** Longer lore for the faction page. */
  lore: { ru: string; en: string };
  /** Visual brand colour — used on banners, gear trim, etc. */
  colour: string;
  /** Two-letter sigil shown on banners + UI badges. */
  sigil: string;
  /** Starting town slug. */
  startTown: string;
  /** Stat bias: which class/role does the faction lean toward? Pure cosmetic for now, will tie into starting gear. */
  bias: 'warrior' | 'ranger' | 'mage' | 'cleric' | 'mixed';
}

export const SIDES: Record<Side, { name: { ru: string; en: string }; colour: string; theme: string }> = {
  tidal: {
    name: { ru: 'Приливные', en: 'Tidals' },
    colour: '#06B6D4',
    theme: 'Народ моря. Торговцы, дипломаты, штормовые маги. Подвижны как сама вода.',
  },
  rooted: {
    name: { ru: 'Укоренённые', en: 'Rooted' },
    colour: '#84CC16',
    theme: 'Горцы и лесники. Кузнецы, защитники, хранители древних традиций. Стоят как скала.',
  },
  anchorless: {
    name: { ru: 'Одичалые', en: 'Anchorless' },
    colour: '#F59E0B',
    theme: 'Кочевники и наёмники. Авантюристы, шпионы, верят что свобода важнее верности. Ветер у них в крови.',
  },
};

export const SUB_FACTIONS: SubFaction[] = [
  // ── TIDALS — 5 Houses ─────────────────────────────────────────
  {
    id: 'house-brinemark',
    side: 'tidal',
    name: { ru: 'Дом Бринмарк', en: 'House Brinemark' },
    blurb: {
      ru: 'Великие торговцы. Считают что золото правит миром.',
      en: 'Great merchants. They believe gold rules the world.',
    },
    lore: {
      ru: 'Бринмарки построили империю на торговых путях между трёмя сторонами. У них шпионы при каждом дворе и склады во всех гаванях. Девиз: «Сделка важнее войны». Но если война неизбежна — у них самый дорогой флот.',
      en: 'The Brinemarks built an empire on the trade lanes between all three sides. Spies at every court, warehouses in every harbor. Motto: «A deal beats a war». But if war is unavoidable — they own the costliest fleet.',
    },
    colour: '#0891B2',
    sigil: 'BM',
    startTown: 'goldreef',
    bias: 'cleric',
  },
  {
    id: 'house-saltcrest',
    side: 'tidal',
    name: { ru: 'Дом Солткрест', en: 'House Saltcrest' },
    blurb: {
      ru: 'Морские воины. Командуют военным флотом Приливных.',
      en: 'Sea warriors. They command the Tidal navy.',
    },
    lore: {
      ru: 'Солткресты — старая воинская семья. Все они с детства учатся фехтовать на качающейся палубе и стрелять из лука против ветра. Их крепость — корабль-цитадель «Серый Шторм». Они презирают тех, кто никогда не нюхал соли.',
      en: 'Saltcrests are an old warrior house. Children learn to fence on rolling decks and shoot bows against the wind. Their fortress is the citadel-ship Grey Storm. They disdain those who have never smelled salt.',
    },
    colour: '#155E75',
    sigil: 'SC',
    startTown: 'greystorm',
    bias: 'warrior',
  },
  {
    id: 'house-stormcaller',
    side: 'tidal',
    name: { ru: 'Дом Стормколлер', en: 'House Stormcaller' },
    blurb: {
      ru: 'Маги бури. Призывают молнии, ветра и волны.',
      en: 'Storm-mages. They call lightning, winds, and waves.',
    },
    lore: {
      ru: 'Стормколлеры — ритуальные маги, которые научились разговаривать с самими Тремя Ветрами. Их башня в гавани Лазурный Глаз — единственное место где встречают всех трёх ветров одновременно. Они часто служат Дому Бринмарк за плату.',
      en: 'Stormcallers are ritual mages who learned to speak with the Three Winds themselves. Their tower at the harbor of Azure Eye is the only place where all three winds meet at once. They often serve House Brinemark for coin.',
    },
    colour: '#0E7490',
    sigil: 'ST',
    startTown: 'azureeye',
    bias: 'mage',
  },
  {
    id: 'house-drowned',
    side: 'tidal',
    name: { ru: 'Дом Утопшие', en: 'House Drowned' },
    blurb: {
      ru: 'Аскеты-провидцы. Видят сны мёртвых моряков.',
      en: 'Ascetic seers. They dream the dreams of drowned sailors.',
    },
    lore: {
      ru: 'Утопшие — секта медиумов. Они погружают новичков в холодную воду пока те не увидят видения. Кто видит — становится «жрецом-утопшим» и говорит с предками флота. Большинство считает их чудиками; несколько раз они спасли Приливных от катастроф предсказаниями.',
      en: 'The Drowned are a sect of mediums. Initiates are submerged in cold water until they see visions. Those who see become «drowned priests» and speak with ancestors of the fleet. Most consider them odd; a few times their prophecies have saved the Tidals from disaster.',
    },
    colour: '#164E63',
    sigil: 'DR',
    startTown: 'silentdepths',
    bias: 'cleric',
  },
  {
    id: 'house-tidewright',
    side: 'tidal',
    name: { ru: 'Дом Тайдрайт', en: 'House Tidewright' },
    blurb: {
      ru: 'Корабельных дел мастера. Без них флот не плавает.',
      en: 'Shipwrights. Without them the fleet does not sail.',
    },
    lore: {
      ru: 'Тайдрайты — главные кораблестроители Стормвейла. Они держат секрет «зелёного дерева», которое не гниёт даже под водой. Никогда не поднимают оружия первыми, но защищать свои верфи будут до последнего гвоздя.',
      en: 'The Tidewrights are Stormveil\'s master shipbuilders. They guard the secret of «green wood» which does not rot even underwater. Never strike first, but defend their docks to the last nail.',
    },
    colour: '#0891B2',
    sigil: 'TW',
    startTown: 'oakdocks',
    bias: 'mixed',
  },

  // ── ROOTED — 5 Clans ──────────────────────────────────────────
  {
    id: 'clan-ironthane',
    side: 'rooted',
    name: { ru: 'Клан Айронтэйн', en: 'Clan Ironthane' },
    blurb: {
      ru: 'Кузнецы. Их клинки переживают своих хозяев.',
      en: 'Smiths. Their blades outlive their owners.',
    },
    lore: {
      ru: 'Айронтэйн держит лучшие кузницы Стормвейла, спрятанные в горном городе Чёрный Молот. Они не продают оружие чужим — только за кровную клятву. Их клинки помнят имена убитых ими врагов.',
      en: 'Ironthane holds Stormveil\'s finest forges in the mountain city of Blackhammer. They sell weapons only by blood oath. Their blades remember the names of the foes they have slain.',
    },
    colour: '#3F6212',
    sigil: 'IT',
    startTown: 'blackhammer',
    bias: 'warrior',
  },
  {
    id: 'clan-stonehearth',
    side: 'rooted',
    name: { ru: 'Клан Стоунхарт', en: 'Clan Stonehearth' },
    blurb: {
      ru: 'Защитники. Их крепости не пали ни разу за тысячу лет.',
      en: 'Defenders. Their fortresses have not fallen in a thousand years.',
    },
    lore: {
      ru: 'Стоунхарты строят стены, ставят гарнизоны, держат осаду. Они не нападают — но если кто-то осадит их крепость, осаждать его будут до конца. Их девиз: «Камень помнит».',
      en: 'Stonehearths build walls, station garrisons, and weather sieges. They do not attack — but if a fortress of theirs is besieged, they will besiege the besieger to the end. Motto: «Stone remembers».',
    },
    colour: '#65A30D',
    sigil: 'SH',
    startTown: 'rampartcrag',
    bias: 'warrior',
  },
  {
    id: 'clan-deepvein',
    side: 'rooted',
    name: { ru: 'Клан Дипвейн', en: 'Clan Deepvein' },
    blurb: {
      ru: 'Шахтёры. Знают каждый туннель под Стормвейлом.',
      en: 'Miners. They know every tunnel beneath Stormveil.',
    },
    lore: {
      ru: 'Дипвейны живут глубже всех. Они находят редкие руды, кристаллы и забытые гробницы. Половина легенд Укоренённых начинается в их шахтах. Они говорят на собственном диалекте, который чужак выучить почти не может.',
      en: 'Deepveins live deepest. They find rare ores, crystals, and forgotten tombs. Half the Rooted legends begin in their mines. They speak their own dialect, nearly unlearnable by outsiders.',
    },
    colour: '#4D7C0F',
    sigil: 'DV',
    startTown: 'irongate',
    bias: 'mixed',
  },
  {
    id: 'clan-hollowoak',
    side: 'rooted',
    name: { ru: 'Клан Холлоуок', en: 'Clan Hollowoak' },
    blurb: {
      ru: 'Друиды. Говорят с деревьями, лечат ранения травой.',
      en: 'Druids. They speak with trees and heal wounds with herbs.',
    },
    lore: {
      ru: 'Холлоуоки живут в Древнем Лесу. Они не вырубают ни одного дерева без обряда. Когда враг входит в их лес — деревья сами становятся его врагами. Многие из их рядов лечат и проклинают одновременно.',
      en: 'Hollowoaks live in the Old Forest. They cut no tree without rite. When an enemy enters their forest, the trees themselves become his foes. Many of their ranks both heal and curse.',
    },
    colour: '#84CC16',
    sigil: 'HO',
    startTown: 'oldforest',
    bias: 'cleric',
  },
  {
    id: 'clan-bloodforge',
    side: 'rooted',
    name: { ru: 'Клан Бладфордж', en: 'Clan Bloodforge' },
    blurb: {
      ru: 'Берсерки. Радикальная фракция Укоренённых.',
      en: 'Berserkers. Radical wing of the Rooted.',
    },
    lore: {
      ru: 'Бладфорджи считают что мир Укоренённых застаивается, и единственный способ возродить силу клана — война. Их боятся даже свои. На полях боя они идут впереди, без щитов, с двумя топорами. Многие не возвращаются. Те, кто возвращается, становятся легендой.',
      en: 'Bloodforges believe the Rooted have grown stagnant, and only war can revive clan strength. Even their own fear them. On the field they charge first — no shields, two axes. Many don\'t return. Those who do become legends.',
    },
    colour: '#86A53D',
    sigil: 'BF',
    startTown: 'crimsonpeak',
    bias: 'warrior',
  },

  // ── ANCHORLESS — 5 Bands ──────────────────────────────────────
  {
    id: 'band-hollow-crown',
    side: 'anchorless',
    name: { ru: 'Банда Полый Венец', en: 'Band of the Hollow Crown' },
    blurb: {
      ru: 'Изгнанная знать. Плетёт интриги, мечтает о троне.',
      en: 'Deposed nobility. Schemes and dreams of thrones.',
    },
    lore: {
      ru: 'Полый Венец — потомки изгнанных королей. Они считают что Стормвейл должен быть единым королевством под их короной. Никто их не воспринимает всерьёз — и в этом их сила. Они говорят на семи языках и помнят родословные пятисот лет.',
      en: 'The Hollow Crown are descendants of deposed kings. They believe Stormveil should be one kingdom under their crown. Nobody takes them seriously — that is their power. They speak seven tongues and remember five hundred years of lineage.',
    },
    colour: '#D97706',
    sigil: 'HC',
    startTown: 'crownsruin',
    bias: 'mage',
  },
  {
    id: 'band-red-compass',
    side: 'anchorless',
    name: { ru: 'Банда Красный Компас', en: 'Band of the Red Compass' },
    blurb: {
      ru: 'Наёмники. Меч за золото, без вопросов.',
      en: 'Mercenaries. Sword for gold, no questions asked.',
    },
    lore: {
      ru: 'Красный Компас работает на любого, кто заплатит. У них чёткий кодекс: не предавать клиента до конца контракта. Половина войн на Стормвейле подписана с ними. Их штаб — таверна «Под Двумя Лунами», известная всем трём сторонам.',
      en: 'The Red Compass works for anyone who pays. Strict code: never betray a client mid-contract. Half of Stormveil\'s wars have their signature. Their HQ is the inn Under Two Moons, known to all three sides.',
    },
    colour: '#B45309',
    sigil: 'RC',
    startTown: 'twomoons',
    bias: 'warrior',
  },
  {
    id: 'band-silent-pact',
    side: 'anchorless',
    name: { ru: 'Банда Молчаливый Пакт', en: 'Band of the Silent Pact' },
    blurb: {
      ru: 'Убийцы и шпионы. Не знают чьи секреты не продали.',
      en: 'Assassins and spies. Have not met a secret they would not sell.',
    },
    lore: {
      ru: 'Молчаливый Пакт — гильдия наёмных убийц. Никто не знает где они живут, как их сосчитать, кто у них главный. Кто увидел члена Пакта в лицо — обычно умирает в течение недели. Иногда — через год.',
      en: 'The Silent Pact is a guild of assassins. Nobody knows where they live, how many they are, or who leads. Anyone who has seen a Pact member\'s face usually dies within a week. Sometimes in a year.',
    },
    colour: '#92400E',
    sigil: 'SP',
    startTown: 'graveshade',
    bias: 'ranger',
  },
  {
    id: 'band-jade-caravan',
    side: 'anchorless',
    name: { ru: 'Банда Нефритовый Караван', en: 'Band of the Jade Caravan' },
    blurb: {
      ru: 'Контрабандисты. Перевезут что угодно, кроме совести.',
      en: 'Smugglers. Will move anything except conscience.',
    },
    lore: {
      ru: 'Нефритовый Караван возит контрабанду между всеми тремя сторонами. У них верблюды, фургоны, маленькие корабли и подземные туннели. Они никогда не торгуют рабами — это единственное, что они отказываются возить. Их атаман Джеймал Звёздная Шапка — легенда.',
      en: 'The Jade Caravan moves contraband between all three sides. Camels, wagons, small ships, underground tunnels. They never trade slaves — the one cargo they refuse. Their captain Jamal Starcap is a legend.',
    },
    colour: '#A16207',
    sigil: 'JC',
    startTown: 'caravangate',
    bias: 'mixed',
  },
  {
    id: 'band-wild-mark',
    side: 'anchorless',
    name: { ru: 'Банда Дикая Метка', en: 'Band of the Wild Mark' },
    blurb: {
      ru: 'Дикари-всадники. Свободны как ветер.',
      en: 'Wild riders. Free as the wind.',
    },
    lore: {
      ru: 'Дикая Метка живёт в Великой Степи. У них нет городов, только лагеря которые перемещаются раз в неделю. Каждый получает татуировку «метку» — свою мету ветра. Метка определяет место в племени. Двух одинаковых меток не бывает.',
      en: 'The Wild Mark lives on the Great Steppe. They have no cities, only camps that move once a week. Each member gets a «mark» tattoo — their personal wind sigil. The mark determines clan rank. No two marks are ever the same.',
    },
    colour: '#CA8A04',
    sigil: 'WM',
    startTown: 'windrun',
    bias: 'ranger',
  },
];

export function subFactionsBySide(side: Side): SubFaction[] {
  return SUB_FACTIONS.filter((f) => f.side === side);
}

export function subFactionById(id: string): SubFaction | undefined {
  return SUB_FACTIONS.find((f) => f.id === id);
}
